import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Page from "../components/Page";
import {
  IconCalendar,
  IconCheck,
  IconAlertTriangle,
  IconPrinter,
  IconDownload,
  IconBuildingBank,
  IconShieldCheck,
  IconCalculator,
  IconScale,
  IconFileText,
} from "@tabler/icons-react";
import { useBalanceSheet } from "../controllers/reports.controller";
import { useStoreSettings } from "../controllers/settings.controller";
import { useTheme } from "../contexts/ThemeContext";

export default function BalanceSheetReportPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { data: storeSettings } = useStoreSettings();
  const store = {
    name: storeSettings?.storeName || storeSettings?.name || "Restaurant & Hotel",
    address: storeSettings?.address,
    phone: storeSettings?.phone,
    panNo: storeSettings?.panNo,
    currency: storeSettings?.currency || "NPR"
  };

  const today = new Date().toISOString().substring(0, 10);
  const [asOfDate, setAsOfDate] = useState(today);

  const { data, isLoading, error } = useBalanceSheet({ asOfDate });

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = async () => {
    if (!data) return;
    try {
      const { Parser } = await import("@json2csv/plainjs");
      const { saveAs } = await import("file-saver");

      const rows = [];
      // Assets
      rows.push({ Section: "ASSETS", Category: "Current Assets", Item: "Cash & Bank", Amount: data.assets.currentAssets.cashAndBank.total.toFixed(2) });
      data.assets.currentAssets.cashAndBank.items.forEach(i => {
        rows.push({ Section: "ASSETS", Category: "Cash & Bank Breakdown", Item: `  - ${i.account}`, Amount: i.amount.toFixed(2) });
      });
      rows.push({ Section: "ASSETS", Category: "Current Assets", Item: "Accounts Receivable", Amount: data.assets.currentAssets.accountsReceivable.toFixed(2) });
      rows.push({ Section: "ASSETS", Category: "Current Assets", Item: "Inventory", Amount: data.assets.currentAssets.inventory.toFixed(2) });
      rows.push({ Section: "ASSETS", Category: "Current Assets", Item: "TOTAL CURRENT ASSETS", Amount: data.assets.currentAssets.total.toFixed(2) });
      rows.push({ Section: "ASSETS", Category: "Fixed Assets", Item: "Property, Plant & Equipment", Amount: data.assets.fixedAssets.total.toFixed(2) });
      rows.push({ Section: "ASSETS", Category: "TOTAL", Item: "TOTAL ASSETS", Amount: data.assets.totalAssets.toFixed(2) });

      // Liabilities
      rows.push({ Section: "LIABILITIES", Category: "Current Liabilities", Item: "Accounts Payable", Amount: data.liabilities.currentLiabilities.accountsPayable.toFixed(2) });
      rows.push({ Section: "LIABILITIES", Category: "Current Liabilities", Item: "Tax Payable", Amount: data.liabilities.currentLiabilities.taxPayable.toFixed(2) });
      rows.push({ Section: "LIABILITIES", Category: "Current Liabilities", Item: "Other Liabilities", Amount: data.liabilities.currentLiabilities.otherLiabilities.total.toFixed(2) });
      rows.push({ Section: "LIABILITIES", Category: "Current Liabilities", Item: "TOTAL CURRENT LIABILITIES", Amount: data.liabilities.currentLiabilities.total.toFixed(2) });
      rows.push({ Section: "LIABILITIES", Category: "TOTAL", Item: "TOTAL LIABILITIES", Amount: data.liabilities.totalLiabilities.toFixed(2) });

      // Equity
      rows.push({ Section: "EQUITY", Category: "Equity", Item: "Owner Capital / Equity", Amount: data.equity.ownerEquity.toFixed(2) });
      rows.push({ Section: "EQUITY", Category: "Equity", Item: "Retained Earnings", Amount: data.equity.retainedEarnings.toFixed(2) });
      rows.push({ Section: "EQUITY", Category: "Equity", Item: "Current Period Net Profit", Amount: data.equity.currentPeriodProfit.toFixed(2) });
      rows.push({ Section: "EQUITY", Category: "TOTAL", Item: "TOTAL EQUITY", Amount: data.equity.totalEquity.toFixed(2) });
      rows.push({ Section: "TOTAL", Category: "TOTAL", Item: "TOTAL LIABILITIES & EQUITY", Amount: data.totalLiabilitiesAndEquity.toFixed(2) });

      const parser = new Parser();
      const csv = parser.parse(rows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, `Balance_Sheet_As_Of_${asOfDate}.csv`);
    } catch (err) {
      console.error(err);
    }
  };

  if (isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center h-96">
          <span className="loading loading-spinner loading-lg text-restro-green"></span>
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <div className="p-8 text-center text-red-500 font-bold">
          Failed to load Balance Sheet: {error.message}
        </div>
      </Page>
    );
  }

  const { assets = {}, liabilities = {}, equity = {}, isBalanced, totalLiabilitiesAndEquity } = data || {};

  return (
    <Page>
      <div className="max-w-7xl mx-auto space-y-6 pb-12">
        {/* Accounting Reports Suite Navigation */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 dark:border-gray-800 pb-4 print:hidden">
          <Link
            to="/dashboard/reports/profit-loss"
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl font-bold text-xs md:text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            <IconCalculator size={17} />
            Profit & Loss Audit
          </Link>
          <Link
            to="/dashboard/reports/trial-balance"
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl font-bold text-xs md:text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            <IconScale size={17} />
            Trial Balance
          </Link>
          <Link
            to="/dashboard/reports/balance-sheet"
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl font-bold text-xs md:text-sm bg-restro-green text-white shadow-md shadow-green-100 dark:shadow-none"
          >
            <IconBuildingBank size={17} />
            Balance Sheet
          </Link>
          <Link
            to="/dashboard/reports/ledger"
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl font-bold text-xs md:text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            <IconFileText size={17} />
            General Ledger
          </Link>
        </div>

        {/* Header and Controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 print:hidden">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-restro-text dark:text-white flex items-center gap-2">
              <IconBuildingBank size={28} className="text-restro-green" />
              Balance Sheet Statement
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Institutional Statement of Financial Position: Assets = Liabilities + Equity
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 p-1 rounded-2xl border border-restro-border-green text-xs font-bold">
              {[
                { id: "today", label: "Today", date: today },
                { id: "eom", label: "End of Month", date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().substring(0, 10) },
                { id: "eoy", label: "End of Year", date: `${new Date().getFullYear()}-12-31` },
              ].map((p) => (
                <button
                  key={p.id}
                  onClick={() => setAsOfDate(p.date)}
                  className={`px-3 py-1.5 rounded-xl transition ${
                    asOfDate === p.date
                      ? "bg-restro-green text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-restro-border-green px-3 py-2 rounded-2xl shadow-sm text-sm">
              <IconCalendar size={18} className="text-gray-400" />
              <span className="text-xs font-bold text-gray-400 uppercase">As of Date:</span>
              <input
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
                className="bg-transparent font-bold outline-none text-xs md:text-sm"
              />
            </div>

            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-restro-gray hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-restro-text rounded-2xl font-bold text-sm transition active:scale-95"
            >
              <IconPrinter size={18} />
              Print
            </button>

            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 bg-restro-green hover:bg-restro-green-button-hover text-white rounded-2xl font-bold text-sm transition active:scale-95 shadow-md shadow-green-100 dark:shadow-none"
            >
              <IconDownload size={18} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Printable White Paper Report Container */}
        <div id="printable-report" className="printable-report space-y-6">
          {/* Professional White Paper Print Letterhead (Print Only) */}
          <div className="hidden print:block text-center pb-4 mb-6 border-b-2 border-black">
            <h1 className="text-2xl font-black uppercase tracking-wider text-black">
              {store.name}
            </h1>
            <p className="text-xs text-gray-700 mt-0.5">
              {[store.address, store.phone && `Phone: ${store.phone}`, store.panNo && `PAN/VAT: ${store.panNo}`].filter(Boolean).join(" • ")}
            </p>
            <div className="mt-3 pt-2 border-t border-gray-400 flex justify-between items-center text-[10pt] font-bold text-black">
              <span className="uppercase tracking-wide font-black">
                STATEMENT OF FINANCIAL POSITION (BALANCE SHEET)
              </span>
              <span className="text-xs font-normal">
                As of: <strong>{asOfDate}</strong>
              </span>
            </div>
            <div className="flex justify-between text-[9pt] text-gray-600 mt-1">
              <span>Printed On: {new Date().toLocaleString()}</span>
              <span>Currency: {store.currency}</span>
            </div>
          </div>

        {/* Balance Status Banner */}
        <div
          className={`p-4 rounded-3xl border flex items-center justify-between ${
            isBalanced
              ? "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300"
              : "bg-rose-50 dark:bg-rose-950/20 border-rose-300 dark:border-rose-800 text-rose-800 dark:text-rose-300"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-white ${
                isBalanced ? "bg-emerald-600" : "bg-rose-600"
              }`}
            >
              {isBalanced ? <IconShieldCheck size={22} /> : <IconAlertTriangle size={22} />}
            </div>
            <div>
              <h4 className="font-black text-sm md:text-base">
                {isBalanced ? "Accounting Equation Verified: Assets = Liabilities + Equity" : "Balance Sheet Equation Discrepancy"}
              </h4>
              <p className="text-xs opacity-80">
                {isBalanced
                  ? `Total Assets (${(assets.totalAssets || 0).toFixed(2)}) perfectly match Total Liabilities & Equity (${(totalLiabilitiesAndEquity || 0).toFixed(2)}).`
                  : `Discrepancy: Difference of ${(data.discrepancy || 0).toFixed(2)}.`}
              </p>
            </div>
          </div>

          <div className="text-right hidden sm:block">
            <div className="text-xs uppercase tracking-widest font-black opacity-70">Total Net Worth</div>
            <div className="text-lg font-black font-mono">
              {(assets.totalAssets || 0).toFixed(2)}
            </div>
          </div>
        </div>

        {/* Two-Column Structured Balance Sheet */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* LEFT COLUMN: ASSETS */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-900 border border-restro-border-green rounded-3xl overflow-hidden shadow-sm">
              <div className="bg-emerald-600 text-white p-4 font-black uppercase tracking-wider text-sm flex items-center justify-between">
                <span>1. ASSETS (सम्पत्ति)</span>
                <span className="font-mono text-base">{(assets.totalAssets || 0).toFixed(2)}</span>
              </div>

              <div className="p-6 space-y-6">
                {/* Current Assets */}
                <div>
                  <h4 className="font-bold text-xs uppercase tracking-widest text-gray-400 mb-3 border-b pb-1">
                    A. Current Assets (चालु सम्पत्ति)
                  </h4>
                  <div className="space-y-2.5 text-sm">
                    {/* Cash & Bank */}
                    <div className="flex justify-between items-center py-1">
                      <span className="font-medium text-restro-text dark:text-gray-200">Cash & Bank Balances</span>
                      <span className="font-mono font-bold text-emerald-600">
                        {(assets.currentAssets?.cashAndBank?.total || 0).toFixed(2)}
                      </span>
                    </div>

                    {/* Breakdown */}
                    {(assets.currentAssets?.cashAndBank?.items || []).map((b, idx) => (
                      <div key={idx} className="flex justify-between text-xs text-gray-500 pl-4 py-0.5">
                        <span>• {b.account}</span>
                        <span className="font-mono">{b.amount.toFixed(2)}</span>
                      </div>
                    ))}

                    {/* Accounts Receivable */}
                    <div className="flex justify-between items-center py-1 border-t border-gray-100 dark:border-gray-800">
                      <div>
                        <span className="font-medium text-restro-text dark:text-gray-200">Accounts Receivable (Customer Credit)</span>
                        <p className="text-[11px] text-gray-400">Unsettled credit invoices & guest ledgers</p>
                      </div>
                      <span className="font-mono font-bold text-emerald-600">
                        {(assets.currentAssets?.accountsReceivable || 0).toFixed(2)}
                      </span>
                    </div>

                    {/* Inventories */}
                    <div className="flex justify-between items-center py-1 border-t border-gray-100 dark:border-gray-800">
                      <div>
                        <span className="font-medium text-restro-text dark:text-gray-200">Inventory Stock Valuation</span>
                        <p className="text-[11px] text-gray-400">Total valuation of current items in stock</p>
                      </div>
                      <span className="font-mono font-bold text-emerald-600">
                        {(assets.currentAssets?.inventory || 0).toFixed(2)}
                      </span>
                    </div>

                    <div className="flex justify-between pt-2 border-t font-bold text-xs bg-gray-50 dark:bg-gray-800/50 p-2 rounded-xl">
                      <span>Total Current Assets:</span>
                      <span className="font-mono font-bold text-emerald-600">
                        {(assets.currentAssets?.total || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Non-Current Assets */}
                <div>
                  <h4 className="font-bold text-xs uppercase tracking-widest text-gray-400 mb-3 border-b pb-1">
                    B. Non-Current / Fixed Assets (स्थिर सम्पत्ति)
                  </h4>
                  <div className="space-y-2.5 text-sm">
                    {(assets.fixedAssets?.items || []).length > 0 ? (
                      assets.fixedAssets.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between py-1 text-xs">
                          <span className="font-medium">{item.account}</span>
                          <span className="font-mono font-bold">{item.amount.toFixed(2)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="flex justify-between py-1 text-xs text-gray-500">
                        <span>Property, Plant & Equipment</span>
                        <span className="font-mono">{(assets.fixedAssets?.total || 0).toFixed(2)}</span>
                      </div>
                    )}

                    <div className="flex justify-between pt-2 border-t font-bold text-xs bg-gray-50 dark:bg-gray-800/50 p-2 rounded-xl">
                      <span>Total Fixed Assets:</span>
                      <span className="font-mono font-bold text-emerald-600">
                        {(assets.fixedAssets?.total || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Total Assets Summary */}
                <div className="p-4 bg-emerald-50 dark:bg-emerald-950/20 border-2 border-emerald-500 rounded-2xl flex justify-between items-center font-black">
                  <span className="text-sm md:text-base uppercase tracking-wider text-emerald-900 dark:text-emerald-200">
                    TOTAL ASSETS
                  </span>
                  <span className="text-xl md:text-2xl font-mono text-emerald-600">
                    {(assets.totalAssets || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: LIABILITIES & EQUITY */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-900 border border-restro-border-green rounded-3xl overflow-hidden shadow-sm">
              <div className="bg-blue-600 text-white p-4 font-black uppercase tracking-wider text-sm flex items-center justify-between">
                <span>2. LIABILITIES & EQUITY (दायित्व तथा पुँजी)</span>
                <span className="font-mono text-base">{(totalLiabilitiesAndEquity || 0).toFixed(2)}</span>
              </div>

              <div className="p-6 space-y-6">
                {/* Liabilities */}
                <div>
                  <h4 className="font-bold text-xs uppercase tracking-widest text-gray-400 mb-3 border-b pb-1">
                    A. Current Liabilities (चालु दायित्व)
                  </h4>
                  <div className="space-y-2.5 text-sm">
                    {/* Accounts Payable */}
                    <div className="flex justify-between items-center py-1">
                      <div>
                        <span className="font-medium text-restro-text dark:text-gray-200">Accounts Payable (Trade Payables)</span>
                        <p className="text-[11px] text-gray-400">Unsettled purchase dues to suppliers</p>
                      </div>
                      <span className="font-mono font-bold text-rose-600">
                        {(liabilities.currentLiabilities?.accountsPayable || 0).toFixed(2)}
                      </span>
                    </div>

                    {/* Tax Payable */}
                    <div className="flex justify-between items-center py-1 border-t border-gray-100 dark:border-gray-800">
                      <div>
                        <span className="font-medium text-restro-text dark:text-gray-200">Taxes & Duties Payable</span>
                        <p className="text-[11px] text-gray-400">VAT, Service Charges & Government collections</p>
                      </div>
                      <span className="font-mono font-bold text-rose-600">
                        {(liabilities.currentLiabilities?.taxPayable || 0).toFixed(2)}
                      </span>
                    </div>

                    {/* Other Liabilities */}
                    {(liabilities.currentLiabilities?.otherLiabilities?.items || []).map((o, idx) => (
                      <div key={idx} className="flex justify-between text-xs text-gray-500 pl-4 py-0.5">
                        <span>• {o.account}</span>
                        <span className="font-mono">{o.amount.toFixed(2)}</span>
                      </div>
                    ))}

                    <div className="flex justify-between pt-2 border-t font-bold text-xs bg-gray-50 dark:bg-gray-800/50 p-2 rounded-xl">
                      <span>Total Liabilities:</span>
                      <span className="font-mono font-bold text-rose-600">
                        {(liabilities.totalLiabilities || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Equity */}
                <div>
                  <h4 className="font-bold text-xs uppercase tracking-widest text-gray-400 mb-3 border-b pb-1">
                    B. Equity & Reserves (स्वामित्व तथा पुँजी)
                  </h4>
                  <div className="space-y-2.5 text-sm">
                    {/* Owner Capital */}
                    <div className="flex justify-between items-center py-1">
                      <span className="font-medium text-restro-text dark:text-gray-200">Owner Capital / Contributed Equity</span>
                      <span className="font-mono font-bold text-blue-600">
                        {(equity.ownerEquity || 0).toFixed(2)}
                      </span>
                    </div>

                    {/* Retained Earnings */}
                    <div className="flex justify-between items-center py-1 border-t border-gray-100 dark:border-gray-800">
                      <span className="font-medium text-restro-text dark:text-gray-200">Retained Earnings (Prior Periods)</span>
                      <span className="font-mono font-bold text-blue-600">
                        {(equity.retainedEarnings || 0).toFixed(2)}
                      </span>
                    </div>

                    {/* Current Period Net Profit */}
                    <div className="flex justify-between items-center py-1 border-t border-gray-100 dark:border-gray-800">
                      <div>
                        <span className="font-medium text-restro-text dark:text-gray-200">Current Period Net Profit / (Loss)</span>
                        <p className="text-[11px] text-gray-400">Reconciled from Profit & Loss statement</p>
                      </div>
                      <span className={`font-mono font-bold ${equity.currentPeriodProfit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {(equity.currentPeriodProfit || 0).toFixed(2)}
                      </span>
                    </div>

                    <div className="flex justify-between pt-2 border-t font-bold text-xs bg-gray-50 dark:bg-gray-800/50 p-2 rounded-xl">
                      <span>Total Equity:</span>
                      <span className="font-mono font-bold text-blue-600">
                        {(equity.totalEquity || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Total Liabilities & Equity Summary */}
                <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border-2 border-blue-500 rounded-2xl flex justify-between items-center font-black">
                  <span className="text-sm md:text-base uppercase tracking-wider text-blue-900 dark:text-blue-200">
                    TOTAL LIABILITIES & EQUITY
                  </span>
                  <span className="text-xl md:text-2xl font-mono text-blue-600">
                    {(totalLiabilitiesAndEquity || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Formal Three-Column Signatures (Prepared By, Verified By, Approved By) */}
        <div className="signature-section mt-16 pt-8 border-t-2 border-black avoid-break">
          <div className="grid grid-cols-3 gap-6 text-center">
            {/* Prepared By */}
            <div className="flex flex-col items-center">
              <div className="w-44 border-b-2 border-black mb-2"></div>
              <p className="font-bold text-xs uppercase tracking-wider text-black dark:text-gray-200">Prepared By</p>
              <p className="text-[11px] text-gray-500">Accountant / Cashier</p>
              <p className="text-[10px] text-gray-400 mt-2">Date: ________________</p>
            </div>

            {/* Verified By */}
            <div className="flex flex-col items-center">
              <div className="w-44 border-b-2 border-black mb-2"></div>
              <p className="font-bold text-xs uppercase tracking-wider text-black dark:text-gray-200">Verified By</p>
              <p className="text-[11px] text-gray-500">Internal Auditor / Finance</p>
              <p className="text-[10px] text-gray-400 mt-2">Date: ________________</p>
            </div>

            {/* Approved By */}
            <div className="flex flex-col items-center">
              <div className="w-44 border-b-2 border-black mb-2"></div>
              <p className="font-bold text-xs uppercase tracking-wider text-black dark:text-gray-200">Approved By</p>
              <p className="text-[11px] text-gray-500">Managing Director / Owner</p>
              <p className="text-[10px] text-gray-400 mt-2">Date: ________________</p>
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* Professional White Paper Print CSS */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 12mm 15mm;
          }
          html, body {
            background: #ffffff !important;
            color: #000000 !important;
            font-size: 10pt !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          nav, aside, header, .appbar, .navbar, .print\\:hidden, button, input, select, a {
            display: none !important;
          }
          div[class*="pl-"], div[class*="md:pl-"], [class*="pl-"] {
            padding-left: 0 !important;
            margin-left: 0 !important;
          }
          body * {
            visibility: hidden;
          }
          #printable-report, #printable-report * {
            visibility: visible;
          }
          #printable-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
            background: #ffffff !important;
            color: #000000 !important;
            padding: 0 !important;
            margin: 0 !important;
            border: none !important;
            box-shadow: none !important;
          }
          #printable-report .grid {
            display: grid !important;
          }
          #printable-report .lg\\:grid-cols-2 {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 16px !important;
          }
          #printable-report .bg-white, #printable-report .dark\\:bg-gray-900 {
            background: #ffffff !important;
            border: 1px solid #000000 !important;
            border-radius: 4px !important;
            box-shadow: none !important;
          }
          #printable-report .bg-emerald-600, #printable-report .bg-amber-600, #printable-report .bg-blue-600 {
            background: #f3f4f6 !important;
            color: #000000 !important;
            border-bottom: 2px solid #000000 !important;
            font-weight: 800 !important;
          }
          #printable-report .text-white {
            color: #000000 !important;
          }
          #printable-report .text-emerald-600,
          #printable-report .text-blue-600,
          #printable-report .text-rose-600,
          #printable-report .text-amber-600 {
            color: #000000 !important;
          }
          .avoid-break {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .signature-section {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin-top: 40px !important;
            padding-top: 15px !important;
          }
        }
      `}</style>
    </Page>
  );
}
